import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';

/**
 * A post, linked to where it actually lives.
 *
 * Every surface that shows a post is a dead end without this: a manager reads
 * "Kim you stole a bunch of copyrights from me" and then has to go and find it
 * by hand on X. Following up is the point of the product, so the post text
 * itself is the link wherever we hold a URL for it.
 *
 * A real anchor, not a button with window.open. The scan page used the latter,
 * which cannot be middle-clicked, opened in a new tab, copied, or reached
 * sensibly by a screen reader - all of which a manager chasing a post wants.
 *
 * `rel="noopener noreferrer"` is not optional on target="_blank": without
 * noopener the opened page gets a handle on this one via window.opener.
 *
 * Falls back to plain text when there is no URL, so a platform that does not
 * give us one (or an older record) degrades to what it showed before rather
 * than rendering a link that goes nowhere.
 */
export function PostLink({ url, children, className, clamp = false, truncate = false, showIcon = true, title }) {
	const body = children || 'View post';

	if (!url) {
		return <span className={cn(clamp && 'line-clamp-2', truncate && 'block truncate', className)}>{body}</span>;
	}

	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			title={title || 'Open the original post'}
			// stopPropagation: these sit inside rows and cards that open a
			// drawer on click. Without it, following the link ALSO opens the
			// drawer behind the new tab.
			onClick={(e) => e.stopPropagation()}
			className={cn(
				'group inline-flex min-w-0 max-w-full items-start gap-1 text-left hover:text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-sm',
				className
			)}
		>
			{/* The clamp/truncate goes on the TEXT, not on a parent: this anchor
			    is a flex container, so a parent's text-overflow never reaches
			    the text inside it and a long post would grow the row again. */}
			<span className={cn('min-w-0', clamp && 'line-clamp-2', truncate && 'truncate')}>{body}</span>
			{showIcon && (
				<ExternalLinkIcon className="mt-0.5 h-3 w-3 shrink-0 opacity-40 transition-opacity group-hover:opacity-90" aria-hidden />
			)}
			<span className="sr-only"> (opens the original post in a new tab)</span>
		</a>
	);
}
